import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { MegaphoneIcon } from '@heroicons/react/24/outline';

const HomeCompositeLoanCTABannerPage = () => {
    const [settings, setSettings] = useState({
        title: 'Ready to Start Building Your Dream Home?',
        description: 'Take the first step towards constructing your perfect home with Aham\'s hassle-free loan process',
        button_text: 'Apply Now',
        button_link: '/contact'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const LOAN_TYPE = 'composite_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/cta-banner/settings/${LOAN_TYPE}`);
            if (response.data.data) {
                setSettings(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching CTA banner settings:', error);
            toast.error('Failed to load CTA banner settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle save
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await axios.put(`/api/admin/cta-banner/settings/${LOAN_TYPE}`, settings);
            toast.success('CTA Banner updated successfully');
        } catch (error) {
            console.error('Error saving CTA banner settings:', error);
            toast.error('Failed to update CTA banner settings');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-8">
                <div className="h-8 bg-slate-200 rounded w-1/4"></div>
                <div className="h-64 bg-slate-200 rounded-2xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">CTA Banner</h1>
                    <p className="text-slate-500 mt-1">Manage the Call-to-Action banner at the bottom of the page</p>
                </div>
                <div className="h-12 w-12 bg-primary-100/50 text-primary-600 rounded-2xl flex items-center justify-center">
                    <MegaphoneIcon className="h-6 w-6" />
                </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Banner Title
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={settings.title}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="Enter banner title"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={settings.description}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                            placeholder="Enter banner description"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Button Text */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Button Text
                            </label>
                            <input
                                type="text"
                                name="button_text"
                                value={settings.button_text}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="e.g., Apply Now"
                            />
                        </div>

                        {/* Button Link */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Button Link
                            </label>
                            <input
                                type="text"
                                name="button_link"
                                value={settings.button_link}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="e.g., /contact"
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </form>
            </div>

        </div>
    );
};

export default HomeCompositeLoanCTABannerPage;
